"use client"

import { Pickaxe, RockingChairIcon as Rock, Gem, Clock } from "lucide-react"
import { motion } from "framer-motion"

export function TodaysMiningReport({ miningReport }) {
  // Animation variants for the section
  const sectionVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
  }

  // Animation variants for cards
  const cardVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (delay) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, delay: delay || 0, ease: "easeOut" },
    }),
    hover: { scale: 1.03, transition: { duration: 0.2 } },
  }

  if (!miningReport) {
    return (
      <motion.section className="mb-8 md:mb-12" initial="hidden" animate="visible" variants={sectionVariants}>
        <div className="flex items-center mb-4 md:mb-6">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 flex items-center">
            <Clock className="h-5 md:h-6 w-5 md:w-6 mr-2 text-blue-600" />
            Today's Mining Report
          </h2>
        </div>

        <motion.div
          className="bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-lg border border-gray-100 overflow-hidden"
          whileHover={{ y: -5, transition: { duration: 0.2 } }}
        >
          <div className="p-6 md:p-8 text-center relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute -right-16 -bottom-16 opacity-5">
              <Pickaxe className="h-48 md:h-64 w-48 md:w-64" />
            </div>

            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="mb-6 mx-auto bg-blue-50 w-16 md:w-20 h-16 md:h-20 rounded-full flex items-center justify-center"
            >
              <Gem className="h-8 md:h-10 w-8 md:w-10 text-blue-600" />
            </motion.div>

            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              <p className="text-gray-500 text-base md:text-lg font-medium mb-2">No report available for today.</p>
              <p className="text-gray-400 text-sm mb-6">Submit a report to track your progress.</p>
            </motion.div>

            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              <motion.button
                className="bg-gradient-to-r from-emerald-600 to-blue-600 text-white font-medium py-2 px-6 rounded-lg shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 mx-auto"
                whileHover={{ scale: 1.05, boxShadow: "0 10px 25px -5px rgba(59, 130, 246, 0.5)" }}
                whileTap={{ scale: 0.98 }}
              >
                <Pickaxe className="h-5 w-5" />
                Start Mining
              </motion.button>
            </motion.div>
          </div>
        </motion.div>
      </motion.section>
    )
  }

  return (
    <motion.section className="mb-8 md:mb-12" initial="hidden" animate="visible" variants={sectionVariants}>
      <h2 className="text-xl md:text-2xl font-bold text-gray-800 flex items-center mb-4 md:mb-6">
        <Clock className="h-5 md:h-6 w-5 md:w-6 mr-2 text-blue-600" />
        Today's Mining Report
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        <motion.div
          className="sm:col-span-1"
          custom={0}
          initial="hidden"
          animate="visible"
          whileHover="hover"
          variants={cardVariants}
        >
          <MiningReportCard
            icon={Pickaxe}
            title="Caving"
            description="Meters Explored Today"
            value={`${miningReport?.caving || 0}m`}
            accentColor="text-blue-600"
            bgColor="bg-gradient-to-br from-blue-50 to-blue-100"
            borderColor="border-blue-200"
            iconBgColor="bg-blue-500/10"
          />
        </motion.div>

        <motion.div
          className="sm:col-span-1"
          custom={0.1}
          initial="hidden"
          animate="visible"
          whileHover="hover"
          variants={cardVariants}
        >
          <MiningReportCard
            icon={Rock}
            title="Useless Stones"
            description="Collected Today"
            value={miningReport?.uselessStones || 0}
            accentColor="text-gray-600"
            bgColor="bg-gradient-to-br from-gray-50 to-gray-100"
            borderColor="border-gray-200"
            iconBgColor="bg-gray-500/10"
          />
        </motion.div>

        <motion.div
          className="sm:col-span-2 lg:col-span-1"
          custom={0.2}
          initial="hidden"
          animate="visible"
          whileHover="hover"
          variants={cardVariants}
        >
          <MiningReportCard
            icon={Gem}
            title="Emeralds Found"
            description="Gems Uncovered"
            value={miningReport?.emeraldsFound || 0}
            accentColor="text-emerald-600"
            bgColor="bg-gradient-to-br from-emerald-50 to-emerald-100"
            borderColor="border-emerald-200"
            iconBgColor="bg-emerald-500/10"
          />
        </motion.div>
      </div>
    </motion.section>
  )
}

function MiningReportCard({ icon: Icon, title, description, value, accentColor, bgColor, borderColor, iconBgColor }) {
  return (
    <div className={`rounded-xl shadow-sm overflow-hidden border ${borderColor} h-full ${bgColor}`}>
      <div className="p-4 md:p-5">
        <div className="flex items-center justify-between mb-2">
          <h3 className={`text-sm font-medium ${accentColor} flex items-center`}>{title}</h3>
          <div className={`${iconBgColor} p-2 rounded-full`}>
            <Icon className={`h-4 w-4 md:h-5 md:w-5 ${accentColor}`} />
          </div>
        </div>
        <p className={`text-2xl md:text-3xl font-bold ${accentColor}`}>{value}</p>
        <p className={`text-xs ${accentColor}/70 mt-1`}>{description}</p>
      </div>
    </div>
  )
}
